import React, { useEffect, useState, type ReactNode, type HTMLAttributes } from "react";
import Box from "@mui/material/Box";
import ToolTipComponent from "../tooltip";
import { untranslated, type TranslatedString } from "@i18n";
import type { SxProps, Theme } from "@mui/material/styles";
import type { TooltipPosition } from "../../interface";

interface TextProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children?: ReactNode | undefined;
  className?: string | undefined;
  /** Tooltip content - strings must be wrapped in t() for internationalization */
  tooltipContent?: TranslatedString | React.ReactElement | undefined;
  alwaysShowTooltip?: boolean | undefined;
  sx?: SxProps<Theme> | undefined;
  placement?: TooltipPosition | undefined;
  grid?: boolean | undefined;
  noWrap?: boolean | undefined;
  hideToolTip?: boolean | undefined;
}

// `as const` so the keyword values keep their literal types: `whiteSpace` and `WebkitBoxOrient` are
// keyword-only in csstype, and a widened `string` is not one of the values MUI's `sx` accepts.
const sxMapper = {
  grid: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
  },
  normal: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "nowrap",
  },
  noWrap: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "normal",
  },
} as const;

/** One entry of MUI's array form of `sx`, read off `SxProps<Theme>` itself so it cannot drift. */
type SxEntry = Extract<SxProps<Theme>, readonly unknown[]>[number];

/**
 * `sx` arrives as a style object, a theme callback, an array of either, or nothing, and the merge
 * below splices it into MUI's array form. `Array.isArray` cannot make that split without erasing
 * the entries — its lib signature is `arg is any[]` while MUI declares the array form
 * `ReadonlyArray<…>`, which is not assignable to `any[]` — so the array form is recognised here
 * instead. An absent `sx` contributes nothing, exactly as the `undefined` entry the previous
 * `[sx]` spliced in did.
 */
const isSxArray = (value: SxProps<Theme>): value is readonly SxEntry[] => Array.isArray(value);
const sxEntries = (value: SxProps<Theme> | undefined): readonly SxEntry[] => {
  if (value === undefined) return [];
  return isSxArray(value) ? value : [value];
};

const Text: React.FC<TextProps> = ({
  children,
  className,
  tooltipContent,
  alwaysShowTooltip = false,
  style = {},
  sx,
  placement,
  grid = false,
  noWrap = false,
  hideToolTip = false,
  ...rest
}) => {
  const [tooltipTitle, setTooltipTitle] = useState<TranslatedString | React.ReactElement | null>(null);

  useEffect(() => {
    if (!tooltipTitle) return;
    const hideTooltip = () => setTooltipTitle(null);
    window.addEventListener("scroll", hideTooltip, true);
    return () => window.removeEventListener("scroll", hideTooltip, true);
  }, [tooltipTitle]);

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    // 1px tolerance: rem sizes under the viewport-clamped root font are fractional, and the rounded scroll/client
    // sizes of a label that exactly fits can differ by a pixel, which showed a tooltip on fully visible text
    const hasOverflow = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;

    // `sx` is also allowed to be a theme callback or an array of style objects; only the plain
    // style-object form can declare a `whiteSpace` up front, which is all this check ever read.
    const sxWhiteSpace = sx && typeof sx === "object" && "whiteSpace" in sx ? sx.whiteSpace : undefined;
    if (alwaysShowTooltip || (!hideToolTip && hasOverflow && (sxWhiteSpace != "normal" || noWrap))) {
      // Children are arbitrary nodes, never a translation key, so they go through the SoT brand
      // helper for dynamic content rather than being asserted into TranslatedString.
      // KNOWN BUG: `children` is `ReactNode`, so `String(children)` yields "[object Object]" for element
      // children and comma-joins array children; only a plain string/number child stringifies usefully.
      // Impact: a truncated <Text> wrapping an element shows a tooltip reading "[object Object]" - e.g.
      // `<Text><Trans i18nKey="items_selected" …/></Text>` at html/SlickGrid/Pagination.tsx:63.
      // Blocked on: narrowing to string|number children here would delete real tooltips from the many call
      // sites that pass string arrays, so which children still get a fallback tooltip is a product call.
      setTooltipTitle(tooltipContent || untranslated(String(children)) || null);
    }
  };

  const handleMouseLeave = () => {
    setTooltipTitle(null);
  };

  return (
    <ToolTipComponent content={tooltipTitle} position={placement ?? "top"}>
      <Box
        component="div"
        className={className}
        // MUI's documented merge for a wrapper that owns base styles and forwards `sx`. Spreading
        // `sx` into one object only ever worked for its plain-object form - the callback and array
        // forms have no own style keys - which is what the erased assertion was hiding.
        sx={[
          {
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "bottom",
            fontFamily: "inherit",
            ...sxMapper[grid ? "grid" : noWrap ? "noWrap" : "normal"],
          },
          ...sxEntries(sx),
          style,
        ]}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Box>
    </ToolTipComponent>
  );
};

export default Text;
